import { HttpClient } from "@/App/services/httpClient/HttpClient";
import storage, { StorageKey, StorageMode } from "@/App/services/storage.service";
import PubnubServiceImpl from "@/App/services/PubnubService";
import config from "@/App/config/main";
import type { UserBroadcastChannelsResponse } from "@/Pages/Login/services/responses/UserBroadcastChannelsResponse";
import type { UserSecretsResponse } from "@/Pages/Login/services/responses/UserSecretsResponse";
import { LiveUpdateService } from "@/App/services/LiveUpdateService";
import type { User, UserPreferences } from "@/App/user/User";
import { userHasFeatureEnabled } from "@/App/user/userHasFeatureEnabled";
import { FeatureFlag } from "@/App/types/FeatureFlag";
import { deepMerge } from "@/App/utils/deepMergeProperties";

const queryParamsForUserMe = {
  includeIftHospitals: true,
  includeIftEmsAgencies: true,
  includeMihEmsAgencies: true,
};

export default {
  retrieveUserData(): Promise<User> {
    return HttpClient.get<User>({ path: "/users/me", params: queryParamsForUserMe }).then(async (user) => {
      const sessionStorage = userHasFeatureEnabled({ user, feature: FeatureFlag.useSessionStorage });
      storage.setMode(sessionStorage ? StorageMode.clearWhenWebSessionEnds : StorageMode.normal);
      storage.save(StorageKey.user, user);
      await this.getAndSaveSecrets();
      await LiveUpdateService.startListeningForLiveUpdate(user);
      await this.retrieveAndSaveBroadcastChannels();
      return user;
    });
  },

  updateUserPreferences(preferences: UserPreferences): User | null {
    const user = this.getUser();
    if (user) {
      deepMerge(user, preferences);
      storage.save(StorageKey.user, user);
    }
    return user;
  },

  getAndSaveSecrets(): Promise<void> {
    return HttpClient.get<UserSecretsResponse>({ path: `${config.API_URL}/secret` }).then(
      (response: UserSecretsResponse) => {
        storage.save(StorageKey.pubnubSubscriptionKey, response.pubnub.api.subscribe, false);
        storage.save(StorageKey.googleMapsApiKey, response.googleMaps.apiKey, false);
      },
    );
  },

  retrieveAndSaveBroadcastChannels(): Promise<void> {
    return HttpClient.get<UserBroadcastChannelsResponse>({
      path: `${config.API_URL}/notification/broadcast/channels/me`,
    }).then((response) => {
      storage.save(StorageKey.broadcastChannels, response.channels, false);
    });
  },

  clearUser(): void {
    storage.removeItem(StorageKey.user);
    storage.removeItem(StorageKey.pubnubSubscriptionKey);
    storage.removeItem(StorageKey.broadcastChannels);
    storage.removeItem(StorageKey.googleMapsApiKey);
  },

  getUser(): User | null {
    return storage.getItemAsObject(StorageKey.user);
  },

  getUserId(): string | null {
    return this.getUser()?.id ?? null;
  },

  getGoogleMapsApiKey(): string {
    return storage.getItem(StorageKey.googleMapsApiKey) ?? "";
  },

  stopAllUserActivity(): void {
    PubnubServiceImpl.getInstance().unsubscribe();
  },
};
